/**
 * BubbleTemplates — BD-style (bande dessinée) bubble template definitions
 * and shared drawing utilities used by both BubbleRenderer (runtime)
 * and EditorLivePreview (editor).
 */
import Phaser from 'phaser';
import { TECH } from '@/ui/TechUI';
import type { BubbleTemplate } from './CinematicTypes';

// ── Template config ──

export interface BubbleTemplateConfig {
  id: BubbleTemplate;
  label: string;
  description: string;
  cornerRadius: number;
  tailStyle: 'pointed' | 'cloud' | 'jagged' | 'none';
  defaultBorderColor: number;
  defaultFillColor: number;
  defaultTextColor: number;
  borderWidth: number;
  fillAlpha: number;
  fontSize: string;
  fontStyle: string;
}

export const BUBBLE_TEMPLATES: Record<BubbleTemplate, BubbleTemplateConfig> = {
  trueBD: {
    id: 'trueBD',
    label: 'True BD',
    description: 'Classic white comic balloon',
    cornerRadius: 18,
    tailStyle: 'pointed',
    defaultBorderColor: 0x000000,
    defaultFillColor: 0xffffff,
    defaultTextColor: 0x000000,
    borderWidth: 2.5,
    fillAlpha: 1,
    fontSize: '15px',
    fontStyle: 'normal',
  },
  speech: {
    id: 'speech',
    label: 'Speech',
    description: 'Classic BD speech balloon',
    cornerRadius: 12,
    tailStyle: 'pointed',
    defaultBorderColor: TECH.ACCENT,
    defaultFillColor: TECH.BG,
    defaultTextColor: 0xffffff,
    borderWidth: 2,
    fillAlpha: 0.92,
    fontSize: '15px',
    fontStyle: 'normal',
  },
  thought: {
    id: 'thought',
    label: 'Thought',
    description: 'Cloud thought bubble',
    cornerRadius: 20,
    tailStyle: 'cloud',
    defaultBorderColor: TECH.ACCENT,
    defaultFillColor: 0x111122,
    defaultTextColor: 0xcccccc,
    borderWidth: 1.5,
    fillAlpha: 0.88,
    fontSize: '14px',
    fontStyle: 'italic',
  },
  shout: {
    id: 'shout',
    label: 'Shout',
    description: 'Sharp, loud burst',
    cornerRadius: 2,
    tailStyle: 'jagged',
    defaultBorderColor: 0xff4444,
    defaultFillColor: 0x1a0a0a,
    defaultTextColor: 0xffffff,
    borderWidth: 3,
    fillAlpha: 0.95,
    fontSize: '16px',
    fontStyle: 'bold',
  },
  whisper: {
    id: 'whisper',
    label: 'Whisper',
    description: 'Soft, dashed outline',
    cornerRadius: 16,
    tailStyle: 'none',
    defaultBorderColor: 0x888899,
    defaultFillColor: 0x0d0d1a,
    defaultTextColor: 0xaaaaaa,
    borderWidth: 1,
    fillAlpha: 0.75,
    fontSize: '13px',
    fontStyle: 'normal',
  },
};

// ── Shared drawing functions ──

/**
 * Draw a BD-style bubble body on a Graphics object.
 * Uses rounded rect for all templates (cornerRadius varies).
 */
export function drawBubbleBody(
  gfx: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  template: BubbleTemplateConfig,
  fillColor: number, fillAlpha: number,
  borderColor: number, borderAlpha: number,
): void {
  const r = template.cornerRadius;

  // Fill
  gfx.fillStyle(fillColor, fillAlpha);
  gfx.fillRoundedRect(x, y, w, h, r);

  // Border
  if (template.id === 'whisper') {
    drawDashedRoundedRect(gfx, x, y, w, h, r, borderColor, borderAlpha, template.borderWidth);
  } else {
    gfx.lineStyle(template.borderWidth, borderColor, borderAlpha);
    gfx.strokeRoundedRect(x, y, w, h, r);
  }
}

/**
 * Draw the bubble tail from bubble edge toward the tail tip.
 * Dispatches on tailStyle (pointed/cloud/jagged/none).
 */
export function drawBubbleTail(
  gfx: Phaser.GameObjects.Graphics,
  fromX: number, fromY: number,
  toX: number, toY: number,
  template: BubbleTemplateConfig,
  fillColor: number, fillAlpha: number,
  borderColor: number, borderAlpha: number,
): void {
  switch (template.tailStyle) {
    case 'pointed':
      drawPointedTail(gfx, fromX, fromY, toX, toY, fillColor, fillAlpha, borderColor, borderAlpha, template.borderWidth);
      break;
    case 'cloud':
      drawCloudTail(gfx, fromX, fromY, toX, toY, fillColor, fillAlpha, borderColor, borderAlpha);
      break;
    case 'jagged':
      drawJaggedTail(gfx, fromX, fromY, toX, toY, fillColor, fillAlpha, borderColor, borderAlpha, template.borderWidth);
      break;
    case 'none':
      break;
  }
}

/**
 * Compute the best tail origin point on the bubble edge closest to the tip.
 * Returns {x, y} on the edge of the bubble rect.
 */
export function computeTailOrigin(
  bx: number, by: number, bw: number, bh: number,
  tipX: number, tipY: number,
): { x: number; y: number } {
  const cx = bx + bw / 2;
  const cy = by + bh / 2;
  const dx = tipX - cx;
  const dy = tipY - cy;

  // Determine which edge the tail should attach to
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    return { x: cx, y: by + bh }; // default: bottom center
  }

  const angle = Math.atan2(dy, dx);
  const halfW = bw / 2;
  const halfH = bh / 2;

  // Clamp to the edge of the rectangle
  let ex: number, ey: number;
  const tanAngle = Math.tan(angle);

  if (Math.abs(dx) * halfH > Math.abs(dy) * halfW) {
    // Hits left or right edge
    ex = dx > 0 ? bx + bw : bx;
    ey = cy + (ex - cx) * tanAngle;
  } else {
    // Hits top or bottom edge
    ey = dy > 0 ? by + bh : by;
    ex = cx + (ey - cy) / tanAngle;
  }

  // Clamp within bubble bounds
  ex = Math.max(bx + 10, Math.min(bx + bw - 10, ex));
  ey = Math.max(by + 5, Math.min(by + bh - 5, ey));

  return { x: ex, y: ey };
}

// ── Tail drawing implementations ──

function drawPointedTail(
  gfx: Phaser.GameObjects.Graphics,
  fromX: number, fromY: number,
  toX: number, toY: number,
  fillColor: number, fillAlpha: number,
  borderColor: number, borderAlpha: number,
  borderWidth: number,
): void {
  // Perpendicular offset for base width
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 2) return;
  const nx = -dy / len;
  const ny = dx / len;
  const baseHalf = 10;

  const x1 = fromX + nx * baseHalf;
  const y1 = fromY + ny * baseHalf;
  const x2 = fromX - nx * baseHalf;
  const y2 = fromY - ny * baseHalf;

  // Fill triangle
  gfx.fillStyle(fillColor, fillAlpha);
  gfx.fillTriangle(x1, y1, x2, y2, toX, toY);

  // Border lines (sides only, not base — base merges with bubble)
  gfx.lineStyle(borderWidth, borderColor, borderAlpha);
  gfx.lineBetween(x1, y1, toX, toY);
  gfx.lineBetween(x2, y2, toX, toY);
}

function drawCloudTail(
  gfx: Phaser.GameObjects.Graphics,
  fromX: number, fromY: number,
  toX: number, toY: number,
  fillColor: number, fillAlpha: number,
  borderColor: number, borderAlpha: number,
): void {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 5) return;

  // 3 shrinking circles from bubble edge to tip
  const sizes = [8, 5, 3];
  for (let i = 0; i < sizes.length; i++) {
    const t = (i + 1) / (sizes.length + 1);
    const cx = fromX + dx * t;
    const cy = fromY + dy * t;
    const r = sizes[i];

    gfx.fillStyle(fillColor, fillAlpha);
    gfx.fillCircle(cx, cy, r);
    gfx.lineStyle(1.5, borderColor, borderAlpha);
    gfx.strokeCircle(cx, cy, r);
  }
}

function drawJaggedTail(
  gfx: Phaser.GameObjects.Graphics,
  fromX: number, fromY: number,
  toX: number, toY: number,
  fillColor: number, fillAlpha: number,
  borderColor: number, borderAlpha: number,
  borderWidth: number,
): void {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 2) return;

  const nx = -dy / len;
  const ny = dx / len;
  const baseHalf = 12;

  // Build zigzag path from base to tip
  const steps = 4;
  const points: { x: number; y: number }[] = [];

  // Left base
  points.push({ x: fromX + nx * baseHalf, y: fromY + ny * baseHalf });

  // Zigzag along left side toward tip
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const midX = fromX + dx * t;
    const midY = fromY + dy * t;
    const spread = baseHalf * (1 - t) * (i % 2 === 0 ? 1.3 : 0.5);
    points.push({ x: midX + nx * spread, y: midY + ny * spread });
  }

  // Tip
  points.push({ x: toX, y: toY });

  // Zigzag back along right side
  for (let i = steps - 1; i > 0; i--) {
    const t = i / steps;
    const midX = fromX + dx * t;
    const midY = fromY + dy * t;
    const spread = baseHalf * (1 - t) * (i % 2 === 0 ? 0.5 : 1.3);
    points.push({ x: midX - nx * spread, y: midY - ny * spread });
  }

  // Right base
  points.push({ x: fromX - nx * baseHalf, y: fromY - ny * baseHalf });

  // Fill
  gfx.fillStyle(fillColor, fillAlpha);
  gfx.beginPath();
  gfx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y);
  }
  gfx.closePath();
  gfx.fillPath();

  // Stroke (sides only)
  gfx.lineStyle(borderWidth, borderColor, borderAlpha);
  gfx.beginPath();
  gfx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i <= steps; i++) {
    gfx.lineTo(points[i].x, points[i].y);
  }
  gfx.moveTo(points[steps].x, points[steps].y);
  for (let i = steps + 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y);
  }
  gfx.strokePath();
}

// ── Dashed rounded rect for whisper template ──

function drawDashedRoundedRect(
  gfx: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  r: number,
  color: number, alpha: number, lineW: number,
  dashLen = 6, gapLen = 4,
): void {
  gfx.lineStyle(lineW, color, alpha);

  // Collect path points along the rounded rect perimeter
  const points: { x: number; y: number }[] = [];
  const cornerSegs = 8;

  // Top-left corner
  for (let i = 0; i <= cornerSegs; i++) {
    const a = Math.PI + (Math.PI / 2) * (i / cornerSegs);
    points.push({ x: x + r + Math.cos(a) * r, y: y + r + Math.sin(a) * r });
  }
  // Top-right corner
  for (let i = 0; i <= cornerSegs; i++) {
    const a = -Math.PI / 2 + (Math.PI / 2) * (i / cornerSegs);
    points.push({ x: x + w - r + Math.cos(a) * r, y: y + r + Math.sin(a) * r });
  }
  // Bottom-right corner
  for (let i = 0; i <= cornerSegs; i++) {
    const a = 0 + (Math.PI / 2) * (i / cornerSegs);
    points.push({ x: x + w - r + Math.cos(a) * r, y: y + h - r + Math.sin(a) * r });
  }
  // Bottom-left corner
  for (let i = 0; i <= cornerSegs; i++) {
    const a = Math.PI / 2 + (Math.PI / 2) * (i / cornerSegs);
    points.push({ x: x + r + Math.cos(a) * r, y: y + h - r + Math.sin(a) * r });
  }

  // Walk along path drawing dashes
  let drawing = true;
  let remaining = dashLen;

  for (let i = 1; i < points.length; i++) {
    let px = points[i - 1].x, py = points[i - 1].y;
    const tx = points[i].x, ty = points[i].y;
    let segDx = tx - px, segDy = ty - py;
    let segLen = Math.sqrt(segDx * segDx + segDy * segDy);

    while (segLen > 0.5) {
      const step = Math.min(remaining, segLen);
      const nx = px + (segDx / segLen) * step;
      const ny = py + (segDy / segLen) * step;

      if (drawing) {
        gfx.beginPath();
        gfx.moveTo(px, py);
        gfx.lineTo(nx, ny);
        gfx.strokePath();
      }

      px = nx; py = ny;
      segDx = tx - px; segDy = ty - py;
      segLen = Math.sqrt(segDx * segDx + segDy * segDy);
      remaining -= step;

      if (remaining <= 0) {
        drawing = !drawing;
        remaining = drawing ? dashLen : gapLen;
      }
    }
  }
}
